// editorStore.ts
// Global editor state for the user editor with undo/redo history.

import { create } from "zustand";

export type ContentData = any;
export type CoverData = any;

type Selected = {
  doc: "content" | "cover";
  elementId: string | null;
};

type Snapshot = {
  contentData: ContentData | null;
  coverData: CoverData | null;
  activeDoc: "content" | "cover";
  contentUnitIndex: number;
  coverPageIndex: number;
  selected: Selected;
};

type State = {
  projectId: number | null;
  projectPublicId: string | null;
  sku: string | null;
  authToken: string | null;

  contentData: ContentData | null;
  coverData: CoverData | null;

  activeDoc: "content" | "cover";
  contentUnitIndex: number;
  coverPageIndex: number;
  pendingInlineEdit: { doc: "content" | "cover"; elementId: string } | null;

  selected: Selected;

  undoStack: Snapshot[];
  redoStack: Snapshot[];

  setProject: (p: { id: number; public_id: string; sku: string }) => void;
  setAuthToken: (token: string | null) => void;
  setContentData: (d: ContentData) => void;
  setCoverData: (d: CoverData) => void;
  selectElement: (doc: "content" | "cover", elementId: string | null) => void;
  setActiveDoc: (d: "content" | "cover") => void;
  setContentUnitIndex: (i: number) => void;
  setCoverPageIndex: (i: number) => void;
  requestInlineEdit: (doc: "content" | "cover", elementId: string) => void;
  clearInlineEditRequest: () => void;
  addContentSpreadUnit: () => void;
  insertContentSpreadUnitAt: (atIndex: number) => void;
  reorderContentUnits: (activeUnitId: string, overUnitId: string) => boolean;
  duplicateContentUnit: (index: number) => void;
  duplicateContentUnitAt: (index: number) => void;
  moveContentUnitToStart: (index: number) => void;
  moveContentUnitToEnd: (index: number) => void;
  deleteContentUnit: (index: number) => void;
  deleteContentUnitAt: (index: number) => void;
  updateElement: (doc: "content" | "cover", elementId: string, patch: Record<string, any>) => void;
  updateElementTransient: (doc: "content" | "cover", elementId: string, patch: Record<string, any>) => void;
  deleteElement: (doc: "content" | "cover", elementId: string) => void;
  bringToFront: (doc: "content" | "cover", elementId: string) => void;
  sendToBack: (doc: "content" | "cover", elementId: string) => void;
  moveForward: (doc: "content" | "cover", elementId: string) => void;
  moveBackward: (doc: "content" | "cover", elementId: string) => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  undo: () => void;
  redo: () => void;
};

function cloneJson<T>(v: T): T {
  return v == null ? v : JSON.parse(JSON.stringify(v));
}

function makeSnapshot(get: () => State): Snapshot {
  const st = get();
  return {
    contentData: cloneJson(st.contentData),
    coverData: cloneJson(st.coverData),
    activeDoc: st.activeDoc,
    contentUnitIndex: st.contentUnitIndex,
    coverPageIndex: st.coverPageIndex,
    selected: { ...st.selected },
  };
}

function pushHistory(set: any, get: any) {
  const snap = makeSnapshot(get);
  set((st: State) => ({
    undoStack: [...st.undoStack, snap].slice(-50),
    redoStack: [],
  }));
}

function updateElementInDoc(docData: any, elementId: string, patch: any): any {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  const newPages = pages.map((p: any) => {
    const els = Array.isArray(p?.elements) ? p.elements : [];
    const newEls = els.map((el: any) => {
      if (el?.id !== elementId) return el;
      return { ...el, ...patch, style: { ...(el.style || {}), ...(patch.style || {}) } };
    });
    return { ...p, elements: newEls };
  });
  return { ...docData, pages: newPages };
}

function deleteElementInDoc(docData: any, elementId: string): any {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  const newPages = pages.map((p: any) => {
    const els = Array.isArray(p?.elements) ? p.elements : [];
    return { ...p, elements: els.filter((e: any) => e?.id !== elementId) };
  });
  return { ...docData, pages: newPages };
}

function findElementLocation(docData: any, elementId: string) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const elements = Array.isArray(pages[pageIndex]?.elements) ? pages[pageIndex].elements : [];
    const elementIndex = elements.findIndex((el: any) => el?.id === elementId);
    if (elementIndex >= 0) return { pageIndex, elementIndex };
  }
  return null;
}

/**
 * Reorder elements within a single page while keeping backgrounds locked at the bottom.
 */
function reorderInPage(elements: any[], from: number, to: number) {
  const next = [...elements];
  const element = next[from];
  if (!element) return next;
  if (element?.is_background) return next;

  const backgroundCount = next.filter((item) => item?.is_background).length;
  const minIndex = backgroundCount > 0 ? backgroundCount : 0;
  const clampedTo = Math.max(minIndex, Math.min(next.length - 1, to));

  next.splice(from, 1);
  next.splice(clampedTo, 0, element);
  return next;
}

function applyElementReorder(docData: any, elementId: string, targetIndex: number) {
  const location = findElementLocation(docData, elementId);
  if (!location) return docData;

  const pages = Array.isArray(docData?.pages) ? [...docData.pages] : [];
  const page = pages[location.pageIndex];
  const elements = Array.isArray(page?.elements) ? [...page.elements] : [];
  const nextElements = reorderInPage(elements, location.elementIndex, targetIndex);

  pages[location.pageIndex] = { ...page, elements: nextElements };
  return { ...docData, pages };
}

function cloneUnitWithNewIds(unit: any) {
  const clone = cloneJson(unit) || { elements: [] };
  clone.unit_id = `u_${Math.random().toString(16).slice(2)}`;
  if (Array.isArray(clone.elements)) {
    clone.elements = clone.elements.map((el: any) => ({
      ...el,
      id: `el_${Math.random().toString(16).slice(2)}`,
    }));
  }
  return clone;
}

function isLockedBookSingle(doc: any, index: number) {
  const layout = String(doc?.meta?.content_layout || "book");
  const units = Array.isArray(doc?.pages) ? doc.pages : [];
  const unit = units[index];
  return layout === "book" && String(unit?.kind || "single") === "single" && (index === 0 || index === units.length - 1);
}

export const useEditorStore = create<State>((set, get) => ({
  projectId: null,
  projectPublicId: null,
  sku: null,
  authToken: null,

  contentData: null,
  coverData: null,

  activeDoc: "content",
  contentUnitIndex: 0,
  coverPageIndex: 0,
  pendingInlineEdit: null,

  selected: { doc: "content", elementId: null },

  undoStack: [],
  redoStack: [],

  setProject: (p) =>
    set({
      projectId: p.id,
      projectPublicId: p.public_id,
      sku: p.sku,
    }),

  setAuthToken: (token) => {
    set({ authToken: token || null });
  },

  setContentData: (d) => {
    pushHistory(set, get);
    set((st) => {
      const units = Array.isArray(d?.pages) ? d.pages : [];
      const nextIndex = Math.max(0, Math.min(st.contentUnitIndex, Math.max(0, units.length - 1)));
      return { contentData: d, contentUnitIndex: nextIndex };
    });
  },

  setCoverData: (d) => {
    pushHistory(set, get);
    set({ coverData: d });
  },

  selectElement: (doc, elementId) => {
    set({ selected: { doc, elementId } });
  },

  setActiveDoc: (d) =>
    set((st) => {
      if (st.activeDoc === d) return st;
      return { activeDoc: d, selected: { doc: d, elementId: null } };
    }),

  setContentUnitIndex: (i) =>
    set((st) => {
      const next = Math.max(0, i);
      if (st.contentUnitIndex === next) return st;
      return { contentUnitIndex: next, selected: { doc: "content", elementId: null } };
    }),

  setCoverPageIndex: (i) =>
    set((st) => {
      const next = Math.max(0, i);
      if (st.coverPageIndex === next) return st;
      return { coverPageIndex: next, selected: { doc: "cover", elementId: null } };
    }),

  requestInlineEdit: (doc, elementId) => set({ pendingInlineEdit: { doc, elementId } }),
  clearInlineEditRequest: () => set({ pendingInlineEdit: null }),

  addContentSpreadUnit: () => {
    const st = get();
    if (!st.contentData) return;

    const layout = String(st.contentData?.meta?.content_layout || "book");
    const units = Array.isArray(st.contentData.pages) ? st.contentData.pages : [];
    const insertAt = layout === "book" ? Math.max(1, units.length - 1) : units.length;
    get().insertContentSpreadUnitAt(insertAt);
  },

  insertContentSpreadUnitAt: (atIndex) => {
    const st = get();
    if (!st.contentData) return;

    const doc = st.contentData;
    const layoutMode = String(doc?.meta?.layout_mode || "pages");
    if (layoutMode !== "book_spreads") return;

    const contentLayout = String(doc?.meta?.content_layout || "book");
    const units = Array.isArray(doc.pages) ? [...doc.pages] : [];
    const newUnit = {
      kind: "spread",
      unit_id: `u_${Math.random().toString(16).slice(2)}`,
      elements: []
    };

    let insertAt = atIndex;

    if (contentLayout === "book") {
      if (units.length < 2) return;
      const last = units.length - 1;
      insertAt = Math.max(1, Math.min(last, insertAt));
      if (insertAt === last) insertAt = last;
    } else {
      insertAt = Math.max(0, Math.min(units.length, insertAt));
    }

    pushHistory(set, get);

    units.splice(insertAt, 0, newUnit);

    set({
      contentData: { ...doc, pages: units },
      contentUnitIndex: insertAt,
      selected: { doc: "content", elementId: null }
    });
  },

  reorderContentUnits: (activeUnitId, overUnitId) => {
    const st = get();
    if (!st.contentData) return false;

    const metaLayout = String(st.contentData?.meta?.layout_mode || "pages");
    if (metaLayout !== "book_spreads") return false;

    const layout = String(st.contentData?.meta?.content_layout || "book");
    const units = Array.isArray(st.contentData.pages) ? [...st.contentData.pages] : [];
    if (units.length < 2) return false;

    const from = units.findIndex((u: any) => u?.unit_id === activeUnitId);
    const to = units.findIndex((u: any) => u?.unit_id === overUnitId);
    if (from < 0 || to < 0 || from === to) return false;

    // Locked indices for book layout: first and last
    const lockedStart = layout === "book" ? 0 : -1;
    const lockedEnd = layout === "book" ? units.length - 1 : -1;

    // Prevent moving locked units or dropping onto locked units.
    if (from === lockedStart || from === lockedEnd) return false;
    if (to === lockedStart || to === lockedEnd) return false;

    pushHistory(set, get);

    // Move item in array.
    const moved = units.splice(from, 1)[0];
    units.splice(to, 0, moved);

    // Keep focus on the moved unit.
    const newFocusIndex = units.findIndex((u: any) => u?.unit_id === activeUnitId);

    set({
      contentData: { ...st.contentData, pages: units },
      contentUnitIndex: newFocusIndex >= 0 ? newFocusIndex : st.contentUnitIndex,
      selected: { doc: "content", elementId: null }
    });

    return true;
  },

  duplicateContentUnit: (index) => {
    const st = get();
    if (!st.contentData) return;

    const pages = Array.isArray(st.contentData.pages) ? [...st.contentData.pages] : [];
    const unit = pages[index];
    if (!unit) return;
    if (isLockedBookSingle(st.contentData, index)) return;

    pushHistory(set, get);

    pages.splice(index + 1, 0, cloneUnitWithNewIds(unit));

    set({
      contentData: { ...st.contentData, pages },
      contentUnitIndex: index + 1,
      selected: { doc: "content", elementId: null },
    });
  },

  duplicateContentUnitAt: (index) => {
    get().duplicateContentUnit(index);
  },

  moveContentUnitToStart: (index) => {
    const st = get();
    const doc = st.contentData;
    if (!doc) return;
    if (String(doc.meta?.layout_mode || "pages") !== "book_spreads") return;

    const layout = String(doc.meta?.content_layout || "book");
    const units = Array.isArray(doc.pages) ? [...doc.pages] : [];
    if (!units[index]) return;

    const minIndex = layout === "book" ? 1 : 0;
    const maxIndex = layout === "book" ? units.length - 2 : units.length - 1;
    if (maxIndex < minIndex) return;
    if (index < minIndex || index > maxIndex) return;
    if (index === minIndex) return;

    pushHistory(set, get);

    const moved = units.splice(index, 1)[0];
    units.splice(minIndex, 0, moved);

    set({
      contentData: { ...doc, pages: units },
      contentUnitIndex: minIndex,
      selected: { doc: "content", elementId: null }
    });
  },

  moveContentUnitToEnd: (index) => {
    const st = get();
    const doc = st.contentData;
    if (!doc) return;
    if (String(doc.meta?.layout_mode || "pages") !== "book_spreads") return;

    const layout = String(doc.meta?.content_layout || "book");
    const units = Array.isArray(doc.pages) ? [...doc.pages] : [];
    if (!units[index]) return;

    const minIndex = layout === "book" ? 1 : 0;
    const maxIndex = layout === "book" ? units.length - 2 : units.length - 1;
    if (maxIndex < minIndex) return;
    if (index < minIndex || index > maxIndex) return;
    if (index === maxIndex) return;

    pushHistory(set, get);

    const moved = units.splice(index, 1)[0];
    units.splice(maxIndex, 0, moved);

    set({
      contentData: { ...doc, pages: units },
      contentUnitIndex: maxIndex,
      selected: { doc: "content", elementId: null }
    });
  },

  deleteContentUnit: (index) => {
    const st = get();
    if (!st.contentData) return;

    const pages = Array.isArray(st.contentData.pages) ? [...st.contentData.pages] : [];
    if (!pages[index]) return;
    if (isLockedBookSingle(st.contentData, index)) return;
    if (pages.length <= 1) return;

    pushHistory(set, get);

    pages.splice(index, 1);
    const nextIndex = Math.max(0, Math.min(st.contentUnitIndex, pages.length - 1));

    set({
      contentData: { ...st.contentData, pages },
      contentUnitIndex: nextIndex,
      selected: { doc: "content", elementId: null },
    });
  },

  deleteContentUnitAt: (index) => {
    get().deleteContentUnit(index);
  },

  updateElement: (doc, elementId, patch) => {
    const st = get();
    if (!elementId) return;

    pushHistory(set, get);

    if (doc === "content" && st.contentData) {
      set({ contentData: updateElementInDoc(st.contentData, elementId, patch) });
    }
    if (doc === "cover" && st.coverData) {
      set({ coverData: updateElementInDoc(st.coverData, elementId, patch) });
    }
  },

  updateElementTransient: (doc, elementId, patch) => {
    const st = get();
    if (!elementId) return;

    if (doc === "content" && st.contentData) {
      set({ contentData: updateElementInDoc(st.contentData, elementId, patch) });
    }
    if (doc === "cover" && st.coverData) {
      set({ coverData: updateElementInDoc(st.coverData, elementId, patch) });
    }
  },

  deleteElement: (doc, elementId) => {
    const st = get();
    if (!elementId) return;

    pushHistory(set, get);

    if (doc === "content" && st.contentData) {
      set({ contentData: deleteElementInDoc(st.contentData, elementId) });
    }
    if (doc === "cover" && st.coverData) {
      set({ coverData: deleteElementInDoc(st.coverData, elementId) });
    }

    set({ selected: { doc, elementId: null } });
  },

  bringToFront: (doc, elementId) => {
    const st = get();
    const docData = doc === "content" ? st.contentData : st.coverData;
    if (!docData || !elementId) return;

    const location = findElementLocation(docData, elementId);
    if (!location) return;

    pushHistory(set, get);

    const nextDoc = applyElementReorder(docData, elementId, (docData.pages?.[location.pageIndex]?.elements || []).length - 1);
    if (doc === "content") set({ contentData: nextDoc });
    else set({ coverData: nextDoc });
    set({ selected: { doc, elementId } });
  },

  sendToBack: (doc, elementId) => {
    const st = get();
    const docData = doc === "content" ? st.contentData : st.coverData;
    if (!docData || !elementId) return;

    const location = findElementLocation(docData, elementId);
    if (!location) return;

    const page = docData.pages?.[location.pageIndex];
    const elements = Array.isArray(page?.elements) ? page.elements : [];
    const backgroundCount = elements.filter((item: any) => item?.is_background).length;

    pushHistory(set, get);

    const nextDoc = applyElementReorder(docData, elementId, backgroundCount);
    if (doc === "content") set({ contentData: nextDoc });
    else set({ coverData: nextDoc });
    set({ selected: { doc, elementId } });
  },

  moveForward: (doc, elementId) => {
    const st = get();
    const docData = doc === "content" ? st.contentData : st.coverData;
    if (!docData || !elementId) return;

    const location = findElementLocation(docData, elementId);
    if (!location) return;

    pushHistory(set, get);

    const nextDoc = applyElementReorder(docData, elementId, location.elementIndex + 1);
    if (doc === "content") set({ contentData: nextDoc });
    else set({ coverData: nextDoc });
    set({ selected: { doc, elementId } });
  },

  moveBackward: (doc, elementId) => {
    const st = get();
    const docData = doc === "content" ? st.contentData : st.coverData;
    if (!docData || !elementId) return;

    const location = findElementLocation(docData, elementId);
    if (!location) return;

    pushHistory(set, get);

    const nextDoc = applyElementReorder(docData, elementId, location.elementIndex - 1);
    if (doc === "content") set({ contentData: nextDoc });
    else set({ coverData: nextDoc });
    set({ selected: { doc, elementId } });
  },

  canUndo: () => get().undoStack.length > 0,
  canRedo: () => get().redoStack.length > 0,

  undo: () => {
    const st = get();
    if (st.undoStack.length === 0) return;

    const current = makeSnapshot(get);
    const prev = st.undoStack[st.undoStack.length - 1];
    const rest = st.undoStack.slice(0, -1);

    set({
      contentData: prev.contentData,
      coverData: prev.coverData,
      activeDoc: prev.activeDoc,
      contentUnitIndex: prev.contentUnitIndex,
      coverPageIndex: prev.coverPageIndex,
      selected: prev.selected,
      undoStack: rest,
      redoStack: [...st.redoStack, current].slice(-50),
    });
  },

  redo: () => {
    const st = get();
    if (st.redoStack.length === 0) return;

    const current = makeSnapshot(get);
    const next = st.redoStack[st.redoStack.length - 1];
    const rest = st.redoStack.slice(0, -1);

    set({
      contentData: next.contentData,
      coverData: next.coverData,
      activeDoc: next.activeDoc,
      contentUnitIndex: next.contentUnitIndex,
      coverPageIndex: next.coverPageIndex,
      selected: next.selected,
      redoStack: rest,
      undoStack: [...st.undoStack, current].slice(-50),
    });
  },
}));
